import type { SyncableRecord } from "./types.js";

/** Deterministic JSON with sorted keys — used as a final, position-independent
 *  tiebreak so merge converges to the same winner on every device. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`;
}

/**
 * Order two versions of the SAME record. Returns >0 if `a` is newer, <0 if older,
 * 0 if identical. Precedence: server-authoritative `rev` (when both numeric and
 * differing) → `updated_at` (do NOT trust device clocks for cross-record ordering,
 * but for the SAME record's two versions wall-clock recency is the intended signal)
 * → a stable content tiebreak so equal-timestamp concurrent edits still converge.
 */
export function compareVersions(a: SyncableRecord, b: SyncableRecord): number {
  if (typeof a.rev === "number" && typeof b.rev === "number" && a.rev !== b.rev) {
    return a.rev < b.rev ? -1 : 1;
  }
  if (a.updated_at !== b.updated_at) {
    return a.updated_at < b.updated_at ? -1 : 1;
  }
  const sa = stableStringify(a);
  const sb = stableStringify(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

export function recordsEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

/** Union two set-valued fields into a deterministically-ordered de-duped array.
 *  Sorting (string then numeric) guarantees both devices produce the same order;
 *  set fields are semantically unordered so display order is not load-bearing. */
export function unionField(a: unknown, b: unknown): unknown[] {
  const merged = [
    ...(Array.isArray(a) ? a : []),
    ...(Array.isArray(b) ? b : []),
  ];
  const uniq = Array.from(new Set(merged));
  uniq.sort((x, y) => {
    if (typeof x === "number" && typeof y === "number") return x - y;
    const sx = String(x);
    const sy = String(y);
    return sx < sy ? -1 : sx > sy ? 1 : 0;
  });
  return uniq;
}
